import { apiClient } from '@littlebird/auth-client';
import { useCallback, useRef, useState } from 'react';

import { getLocalStorageItem, setLocalStorageItem } from '@/utils/localStorage';

import type {
  KGAgentMessageBlock,
  KGAgentMessage,
  KGAgentSession,
} from './world-model.types';

const STORAGE_KEY = 'world-model-kg-agent-session';

function loadSession(): KGAgentSession {
  const stored = getLocalStorageItem(STORAGE_KEY);
  if (stored && Array.isArray(stored.messages)) {
    return stored as KGAgentSession;
  }
  return { messages: [], createdAt: Date.now() };
}

function saveSession(session: KGAgentSession): void {
  setLocalStorageItem(STORAGE_KEY, session);
}

export function useKGAgentSession() {
  const [session, setSession] = useState<KGAgentSession>(loadSession);
  const [isStreaming, setIsStreaming] = useState(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const addMessage = useCallback((message: KGAgentMessage) => {
    setSession((prev: KGAgentSession) => {
      const updated = { ...prev, messages: [...prev.messages, message] };
      saveSession(updated);
      return updated;
    });
  }, []);

  const appendBlockToLastAssistant = useCallback(
    (block: KGAgentMessageBlock) => {
      setSession((prev: KGAgentSession) => {
        const messages = [...prev.messages];
        const lastIndex = messages.length - 1;

        if (lastIndex >= 0 && messages[lastIndex].role === 'assistant') {
          const assistantMessage = messages[lastIndex];
          const existingBlocks = assistantMessage.blocks ?? [];
          const updatedBlocks = [...existingBlocks];
          const lastBlock = updatedBlocks[updatedBlocks.length - 1];

          if (block.type === 'text' && lastBlock?.type === 'text') {
            updatedBlocks[updatedBlocks.length - 1] = {
              ...lastBlock,
              content: lastBlock.content + block.content,
            };
          } else if (
            block.type === 'thought' &&
            lastBlock?.type === 'thought'
          ) {
            updatedBlocks[updatedBlocks.length - 1] = {
              ...lastBlock,
              content: lastBlock.content + block.content,
            };
          } else {
            updatedBlocks.push(block);
          }

          messages[lastIndex] = {
            ...assistantMessage,
            blocks: updatedBlocks,
            content:
              block.type === 'text'
                ? assistantMessage.content + block.content
                : assistantMessage.content,
          };
        }

        const updated = { ...prev, messages };
        saveSession(updated);
        return updated;
      });
    },
    []
  );

  const sendMessage = useCallback(
    async (content: string) => {
      if (!content.trim() || isStreaming) return;

      const userMessage: KGAgentMessage = {
        role: 'user',
        content: content.trim(),
        blocks: [],
        timestamp: Date.now(),
      };
      addMessage(userMessage);

      const assistantMessage: KGAgentMessage = {
        role: 'assistant',
        content: '',
        blocks: [],
        timestamp: Date.now(),
      };
      addMessage(assistantMessage);

      setIsStreaming(true);

      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      try {
        const history = session.messages.map((m: KGAgentMessage) => ({
          role: m.role,
          content: m.content,
        }));

        const response = await apiClient.fetch('/world_model/kg-agent/chat', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ message: content.trim(), history }),
          signal: abortController.signal,
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const reader = response.body?.getReader();
        if (!reader) throw new Error('No response body');

        const decoder = new TextDecoder();
        let lineBuffer = '';
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          lineBuffer += decoder.decode(value, { stream: true });
          const lines = lineBuffer.split('\n');
          // Hold the last (potentially incomplete) line in the buffer
          lineBuffer = lines.pop() ?? '';

          for (const line of lines) {
            if (!line.startsWith('data: ')) continue;
            try {
              const event = JSON.parse(line.slice(6));
              if (event.type === 'token') {
                appendBlockToLastAssistant({
                  type: 'text',
                  content: event.content,
                });
              } else if (event.type === 'thought') {
                appendBlockToLastAssistant({
                  type: 'thought',
                  content: event.content,
                });
              } else if (event.type === 'tool_call') {
                appendBlockToLastAssistant({
                  type: 'tool_call',
                  toolCall: {
                    name: event.name,
                    arguments: event.arguments,
                    result: event.result,
                  },
                });
              }
            } catch {
              // Skip malformed lines
            }
          }
        }
      } catch (error) {
        if (error instanceof Error && error.name === 'AbortError') {
          appendBlockToLastAssistant({ type: 'interrupted' });
        } else {
          const errorMessage =
            error instanceof Error ? error.message : 'Unknown error';
          appendBlockToLastAssistant({
            type: 'text',
            content: `Error: ${errorMessage}`,
          });
        }
      } finally {
        abortControllerRef.current = null;
        setIsStreaming(false);
      }
    },
    [session.messages, isStreaming, addMessage, appendBlockToLastAssistant]
  );

  const stopStreaming = useCallback(() => {
    abortControllerRef.current?.abort();
  }, []);

  const clearSession = useCallback(() => {
    const fresh: KGAgentSession = { messages: [], createdAt: Date.now() };
    setSession(fresh);
    saveSession(fresh);
  }, []);

  return {
    messages: session.messages,
    isStreaming,
    sendMessage,
    stopStreaming,
    clearSession,
  };
}
